import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import {
  AccountListRowData,
  ColumnId,
} from '@/experiences/sales-portal/domain/accounts/screens/account-list/types';
import { AccountListTableRow } from '@/experiences/sales-portal/domain/accounts/screens/account-list/components/account-list-table/account-list-table-row';
import { Avatar, createAvatarProps } from '@usepepper/ui/atoms/avatar';
import {
  createColumnHelper,
  flexRender,
  HeaderGroup,
  Row,
} from '@tanstack/react-table';
import { createStyles } from '@/contexts/theme-context';
import { CustomColumnDef } from '@experiences/components/table/types';
import {
  ESTIMATED_HEADER_HEIGHT,
  ESTIMATED_ROW_HEIGHT,
  LAST_FULFILLED_COLUMN_PERCENTAGE,
  MIN_COLUMN_SIZE,
  MIN_NAME_COLUMN_SIZE,
  NAME_COLUMN_PERCENTAGE,
} from '@/experiences/sales-portal/domain/accounts/screens/account-list/components/account-list-table/account-list-table-columns';
import { LayoutChangeEvent, View } from 'react-native';
import { Table } from '@experiences/components/table/table';
import { TableHeaderCell } from '@experiences/components/table/table-header-cell';
import { TableHeaderRow } from '@experiences/components/table/table-header-row';
import { Typography } from '@usepepper/ui/atoms/typography';

const columnHelper = createColumnHelper<AccountListRowData>();
const PINNED_COLUMNS = { left: [ColumnId.Name] };

interface AccountListTableProps {
  data: AccountListRowData[];
  emptyComponent?: React.ReactNode;
  listFooter?: React.ReactNode;
  onEndReached?: () => void;
  onRowClick: (row: AccountListRowData) => void;
}

export const AccountListTable: React.FC<AccountListTableProps> = ({
  data,
  emptyComponent,
  listFooter,
  onEndReached,
  onRowClick,
}) => {
  const [styles, theme] = useStyles();
  const [layoutWidth, setLayoutWidth] = useState<number | null>(null);

  const handleLayout = (event: LayoutChangeEvent) => {
    setLayoutWidth(event.nativeEvent.layout.width);
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.restaurantName, {
        cell: (info) => {
          const row = info.row.original;
          const avatarProps = createAvatarProps(
            row.restaurantName,
            row.restaurantUUID,
          );
          const subtitle = row.accountId
            ? `${row.accountId} \u2022 ${row.restaurantAddress}`
            : row.restaurantAddress;

          return (
            <View style={styles.nameCellContainer}>
              <Avatar {...avatarProps} size="medium" />
              <View style={styles.nameCellText}>
                <Typography numberOfLines={1} variant="paragraph-md-600">
                  {row.restaurantName}
                </Typography>
                <Typography
                  color={theme.sk.color.text.tertiary}
                  numberOfLines={1}
                  variant="paragraph-sm"
                >
                  {subtitle}
                </Typography>
              </View>
            </View>
          );
        },
        enableSorting: false,
        header: () => i18n.t('sales_portal.account_list.table.name'),
        id: ColumnId.Name,
        meta: { initialPercentWidth: NAME_COLUMN_PERCENTAGE },
        minSize: MIN_NAME_COLUMN_SIZE,
      }),
      columnHelper.accessor((row) => row.lastFulfillmentDate?.display, {
        cell: (info) => (
          <Typography variant="paragraph-md">
            {info.row.original.lastFulfillmentDate?.display ?? '\u2014'}
          </Typography>
        ),
        enableSorting: false,
        header: () => i18n.t('sales_portal.account_list.table.last_fulfilled'),
        id: ColumnId.LastFulfilled,
        meta: { initialPercentWidth: LAST_FULFILLED_COLUMN_PERCENTAGE },
        minSize: MIN_COLUMN_SIZE,
      }),
    ],
    [styles, theme],
  );

  const renderHeaderRow = useCallback(
    ({ headerGroup }: { headerGroup: HeaderGroup<AccountListRowData> }) => (
      <TableHeaderRow key={headerGroup.id}>
        {headerGroup.headers.map((header) => (
          <TableHeaderCell
            columnId={header.id}
            key={header.id}
            pinnedOffset={
              header.column.getIsPinned()
                ? header.column.getStart('left')
                : undefined
            }
          >
            {header.isPlaceholder ? null : (
              <Typography
                color={theme.sk.color.text.tertiary}
                variant="paragraph-sm-600"
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </Typography>
            )}
          </TableHeaderCell>
        ))}
      </TableHeaderRow>
    ),
    [theme.sk.color.text.tertiary],
  );

  const renderRow = useCallback(
    ({ index, row }: { index: number; row: Row<AccountListRowData> }) => (
      <AccountListTableRow
        index={index}
        key={row.id}
        onRowClick={onRowClick}
        row={row}
      />
    ),
    [onRowClick],
  );

  if (!layoutWidth) {
    return (
      <View
        accessibilityLabel={i18n.t('accounts')}
        accessibilityRole="progressbar"
        accessibilityState={{ busy: true }}
        accessible
        onLayout={handleLayout}
        style={styles.tableWrapper}
      />
    );
  }

  return (
    <View onLayout={handleLayout} style={styles.tableWrapper}>
      <Table
        bodyContainerStyle={styles.tableBodyContainerStyle}
        cellPadding={Array(columns.length).fill(theme.sk.spacing.spaceMedium)}
        columns={columns as CustomColumnDef<AccountListRowData>[]}
        data={data}
        emptyComponent={emptyComponent}
        estimatedHeaderHeight={ESTIMATED_HEADER_HEIGHT}
        estimatedRowHeight={ESTIMATED_ROW_HEIGHT}
        gutters={0}
        initialState={{ columnPinning: PINNED_COLUMNS }}
        // Only one table on this screen, so it's always the active one.
        isActive
        layoutWidth={layoutWidth}
        listFooter={listFooter}
        onEndReached={onEndReached}
        renderHeaderRow={renderHeaderRow}
        renderRow={renderRow}
      />
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  nameCellContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
  },
  nameCellText: {
    flex: 1,
  },
  tableBodyContainerStyle: {
    paddingBottom: ESTIMATED_ROW_HEIGHT,
  },
  tableWrapper: {
    flex: 1,
  },
}));
